import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthContext, useAuthProvider } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';
import { Sidebar } from './components/layout/Sidebar';
import { OskiChat } from './components/chat/OskiChat';
import { AdminPage } from './pages/AdminPage';
import { AgentConfigPage } from './pages/AgentConfigPage';
import { AppsConfigPage } from './pages/AppsConfigPage';
import { AuditPage } from './pages/AuditPage';
import { KanbanBoard } from './components/tickets/KanbanBoard';
import './styles/globals.css';
import styles from './App.module.css';

// ─── Pages ────────────────────────────────────────────────────────────────
function LoginPage() {
  const { login, loading } = useAuthProvider();
  return (
    <div className={styles.loginPage}>
      <div className={styles.loginCard}>
        <div className={styles.loginLogo}>O</div>
        <h1 className={styles.loginTitle}>Oski</h1>
        <p className={styles.loginSub}>AI Workspace · Broota Ecosystem</p>
        <button className={styles.loginBtn} onClick={login} disabled={loading}>
          <GoogleIcon />
          {loading ? 'Cargando…' : 'Entrar con Google Workspace'}
        </button>
        <p className={styles.loginNote}>Solo cuentas @broota.com</p>
      </div>
    </div>
  );
}

// OskiChat se monta en el layout (siempre activo) — no aquí
function DashboardPage() { return null; }

function TicketsPage() {
  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <h1 className={styles.pageTitle}>Tickets</h1>
        <div className={styles.topbarActions} />
      </div>
      <div className={styles.content}>
        <KanbanBoard />
      </div>
    </div>
  );
}

function AdminPageWrapper() {
  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <h1 className={styles.pageTitle}>Gestión de equipo</h1>
        <p className={styles.pageSubtitle}>Roles · Permisos · Acceso a apps</p>
      </div>
      <div className={styles.adminLayout}>
        <AdminPage />
      </div>
    </div>
  );
}

function AgentConfigPageWrapper() {
  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <h1 className={styles.pageTitle}>Configuración de agentes</h1>
        <p className={styles.pageSubtitle}>Prompts · Memoria · Modelo · Parámetros</p>
      </div>
      <div className={styles.adminLayout}>
        <AgentConfigPage />
      </div>
    </div>
  );
}

function AppsConfigPageWrapper() {
  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <h1 className={styles.pageTitle}>Aplicaciones</h1>
        <p className={styles.pageSubtitle}>Colores · Identidad visual</p>
      </div>
      <div className={styles.adminLayout}>
        <AppsConfigPage />
      </div>
    </div>
  );
}

function AuditPageWrapper() {
  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <h1 className={styles.pageTitle}>Auditoría de agentes</h1>
        <p className={styles.pageSubtitle}>PRs · Outcomes · Bug classes · Responsables</p>
      </div>
      <div className={styles.adminLayout}>
        <AuditPage />
      </div>
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────
function AppLayout() {
  const [sidebarOpen, setSidebarOpen]           = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const toggleCollapsed = () => setSidebarCollapsed(v => !v);
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <div className={styles.appLayout}>
      <Sidebar
        isOpen={sidebarOpen}
        isCollapsed={sidebarCollapsed}
        onClose={() => setSidebarOpen(false)}
        onToggle={toggleCollapsed}
      />

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className={styles.overlay}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className={`${styles.appMain} ${sidebarCollapsed ? styles.sidebarClosed : ''}`}>
        {/* Mobile-only topbar */}
        <div className={styles.mobileHeader}>
          <button
            className={styles.menuBtn}
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú">
            <IconMenu />
          </button>
          <span className={styles.mobileLogo}>Oski</span>
        </div>

        {/* ── Oski: siempre montado para no perder el stream SSE al navegar ── */}
        <div
          className={styles.page}
          style={{ display: isHome ? 'flex' : 'none' }}
          aria-hidden={!isHome}
        >
          <div className={styles.topbar}>
            <h1 className={styles.pageTitle}>Oski</h1>
            <p className={styles.pageSubtitle}>Orquestador de equipo · Pame · Dully</p>
          </div>
          <div className={styles.oskiLayout}>
            <OskiChat />
          </div>
        </div>

        {/* ── Otras páginas ─────────────────────────────────────────────────── */}
        <Routes>
          <Route path="/"               element={null} />
          <Route path="/tickets"        element={<TicketsPage />} />
          <Route path="/admin"          element={<AdminPageWrapper />} />
          <Route path="/admin/config"   element={<AgentConfigPageWrapper />} />
          <Route path="/admin/apps"     element={<AppsConfigPageWrapper />} />
          <Route path="/sysadmin/audit" element={<AuditPageWrapper />} />
          <Route path="/admin/audit"    element={<Navigate to="/sysadmin/audit" replace />} />
          <Route path="*"               element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────
export default function App() {
  const auth = useAuthProvider();

  return (
    <AuthContext.Provider value={auth}>
      <ToastProvider>
      <BrowserRouter>
        {auth.loading ? (
          <div className={styles.splash}>
            <div className={styles.splashLogo}>O</div>
          </div>
        ) : auth.firebaseUser ? (
          <AppLayout />
        ) : (
          <LoginPage />
        )}
      </BrowserRouter>
      </ToastProvider>
    </AuthContext.Provider>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16"/>
    </svg>
  );
}
